/**
 * Ce qui attend l'accord du patron.
 *
 * `lister_actions_en_attente` existait depuis la phase 2 et **n'était appelé
 * par aucun écran**. La conséquence n'était pas un défaut d'affichage : c'était
 * la moitié du modèle de rôles qui ne fonctionnait pas. Un conducteur qui
 * préparait un envoi lisait « Demande soumise au patron », et le patron n'avait
 * nulle part où la voir.
 *
 * Depuis le 11 septembre une demande n'expire plus : elle attend jusqu'à ce
 * qu'elle soit exécutée ou refusée. La péremption ne court qu'après le oui, et
 * elle ne borne que l'exécution — voir la migration
 * `20260301002700_pending_actions_until_done`.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DomainError } from "@dker/core";
import type { PendingAction } from "@dker/core";
import { confirmationServices, serviceDeps, useExecutionContext } from "@/services";
import { useTenantOptional } from "@/hooks/useTenant";
import { useUiStore } from "@/stores/uiStore";

const CLE = "actions-en-attente";

function toMessage(error: unknown): string {
  if (error instanceof DomainError) return error.message;
  return "Une erreur inattendue est survenue. Réessayez dans un instant.";
}

export function usePendingActions() {
  const tenant = useTenantOptional();
  const makeContext = useExecutionContext();

  return useQuery<PendingAction[]>({
    queryKey: [CLE, tenant?.id],
    enabled: !!tenant,
    queryFn: () =>
      confirmationServices.listerActionsEnAttente(makeContext(), {}, serviceDeps),
    // Une demande peut arriver d'un autre poste — c'est même son cas d'usage :
    // le conducteur prépare depuis le chantier, le patron voit depuis le
    // bureau. Une liste figée jusqu'au prochain rechargement raterait
    // exactement ce pour quoi elle existe.
    refetchInterval: 60_000,
    staleTime: 0,
  });
}

/**
 * Les clés que toute décision fait bouger.
 *
 * Large à dessein : une action à effet externe touche la facture, l'encours, le
 * tableau de bord et la file du soir, et on ne sait pas laquelle depuis cette
 * liste générique — c'est le propre d'un registre qui porte n'importe quel
 * verbe.
 */
function cles(tenantId: string | undefined) {
  return [
    [CLE, tenantId],
    ["invoices", tenantId],
    ["invoice"],
    ["quotes", tenantId],
    ["quote"],
    ["receivables", tenantId],
    ["day-summary", tenantId],
    ["file-du-soir", tenantId],
    ["material-orders", tenantId],
    // L'agent y prépare aussi des paiements et des dépenses.
    ["expenses", tenantId],
  ];
}

/**
 * Dire oui : confirmer puis exécuter, dans la foulée.
 *
 * Les deux temps restent distincts en base — c'est la confirmation qui engage,
 * l'exécution qui agit — mais un patron qui a lu le résumé et cliqué « Valider »
 * a répondu aux deux questions d'un coup. Les séparer à l'écran ajouterait un
 * clic sans ajouter une décision.
 */
export function useApprovePendingAction() {
  const qc = useQueryClient();
  const tenant = useTenantOptional();
  const makeContext = useExecutionContext();
  const addToast = useUiStore((s) => s.addToast);

  return useMutation({
    mutationFn: async (action: PendingAction) => {
      // Une action déjà confirmée dont l'exécution a échoué se rejoue sans
      // redemander le oui : il a été donné, et `confirm_pending_action`
      // refuserait une seconde fois de toute façon.
      if (action.status !== "confirmee") {
        await confirmationServices.confirmerAction(
          makeContext(),
          { id: action.id },
          serviceDeps,
        );
      }
      return confirmationServices.executerActionConfirmee(
        makeContext(),
        { id: action.id },
        serviceDeps,
      );
    },
    onSuccess: () => {
      for (const k of cles(tenant?.id)) void qc.invalidateQueries({ queryKey: k });
      addToast({ type: "success", title: "Action exécutée" });
    },
    onError: (error) => {
      // La demande reste ouverte : l'échec peut venir d'un SIRET manquant ou
      // d'une étape sautée, choses qui se corrigent puis se réessaient. C'est
      // précisément pour ça qu'elle ne doit pas disparaître de la liste.
      for (const k of cles(tenant?.id)) void qc.invalidateQueries({ queryKey: k });
      addToast({ type: "error", title: "Exécution impossible", message: toMessage(error) });
    },
  });
}

export function useRejectPendingAction() {
  const qc = useQueryClient();
  const tenant = useTenantOptional();
  const makeContext = useExecutionContext();
  const addToast = useUiStore((s) => s.addToast);

  return useMutation({
    mutationFn: (id: string) =>
      confirmationServices.refuserAction(makeContext(), { id }, serviceDeps),
    onSuccess: () => {
      for (const k of cles(tenant?.id)) void qc.invalidateQueries({ queryKey: k });
      addToast({ type: "info", title: "Demande refusée" });
    },
    onError: (error) =>
      addToast({ type: "error", title: "Refus impossible", message: toMessage(error) }),
  });
}
